/**
 * @author Dec Norton <decnorton@gmail.com>
 */

import { Config } from '@app/services/config-provider';
import { isString } from 'lodash-es';

export const TARGET_LOCAL = 'local';
export const TARGET_GLOBAL = 'global';

export function eventEmitterProvider() {
    this.$get = $get;

    $get.$inject = ['$injector'];

    function $get($injector) {
        return $injector.instantiate(EventEmitterService);
    }
}

/**
 * @property {Object} registeredEventTypes
 */
export class EventEmitterService {
    localStorage: Storage;

    private registeredEventTypes: any;
    private wildcardRegistrations: any[];
    private wildcardListener: any;

    static get $inject() {
        return ['$config', '$log', '$rootScope', '$window'];
    }

    private config: Config;

    /**
     * @param $config
     * @param $log
     * @param $rootScope
     * @param {WindowLocalStorage} $window
     */
    constructor(protected $config: Config, protected $log: ng.ILogService, protected $rootScope: ng.IRootScopeService, protected $window: ng.IWindowService) {
        this.config = $config;
        this.$log = $log;
        this.$rootScope = $rootScope;
        this.$window = $window;
        this.localStorage = $window.localStorage;

        this.registeredEventTypes = {};
        this.wildcardRegistrations = [];

        this._onStorageMessageReceived = this._onStorageMessageReceived.bind(this);

        if (this.localStorage) {
            this.$window.addEventListener('storage', this._onStorageMessageReceived, false);
        }
    }

    /**
     * Returns true if the event type has been registered.
     * @private
     * @param {String} eventName
     * @returns {boolean}
     */
    _isAllowed(eventName) {
        return this.registeredEventTypes.hasOwnProperty(eventName);
    }

    /**
     * @param listener
     */
    all(listener) {
        if (listener === this.wildcardListener) {
            return;
        }

        // Remove all existing registrations.
        this.wildcardRegistrations.forEach(remove => remove());

        // Set the wildcard listener.
        this.wildcardListener = listener;

        // Hook up any registered event types
        this.wildcardRegistrations = Object.keys(this.registeredEventTypes).map(eventType =>
            this.$rootScope.$on(eventType, listener),
        );
    }

    /**
     * @param {$rootScope.Scope} $scope
     * @param {string|string[]} eventName
     * @param {function(event, ...args)} listener
     * @param {?boolean} includeGlobal
     * @return {function|function[]} De-registration function.
     */
    on($scope: angular.IScope, eventName: string, listener: (event: angular.IAngularEvent, ...args: any[]) => any, { includeGlobal = true } = {}) {
        if (Array.isArray(eventName)) {
            return eventName
                .map(event => {
                    if (isString(event)) {
                        return this.on($scope, event, listener, { includeGlobal });
                    }
                })
                .filter(Boolean);
        }

        if (!this._isAllowed(eventName)) {
            this.$log.warn(`Event [${eventName}] isn't registered`);
        }

        return $scope.$on(eventName, function ($event, ...args) {
            if (!includeGlobal && args[0].$$target === TARGET_GLOBAL) {
                return;
            }

            listener.apply(null, [$event, ...args]);
        });
    }

    /**
     * Broadcast an event.
     * @param {String} eventName
     * @param {Object} data
     * @returns {Object|bool}
     */
    broadcast(eventName, data: any = {}) {
        if (!this._isAllowed(eventName)) {
            this.$log.error(`Event '${eventName} is not registered'`);
            return false;
        }

        const registration = this.registeredEventTypes[eventName];

        if (registration) {
            this.$rootScope.$broadcast(eventName, {
                ...data,
                $$target: TARGET_LOCAL,
            });

            if (registration.global) {
                this._postMessage(eventName, data);
            }
        }
    }

    /**
     * Register an allowed event type.
     * @param {String} eventName
     * @param {?boolean} global
     */
    registerEventType(eventName, { global = false } = {}) {
        if (this.registeredEventTypes.hasOwnProperty(eventName)) {
            // Already registered
            return;
        }

        this.registeredEventTypes[eventName] = {
            global,
        };

        if (this.wildcardListener) {
            this.wildcardRegistrations.push(this.$rootScope.$on(eventName, this.wildcardListener));
        }

        if (this.config.debug) {
            this.$rootScope.$on(eventName, (event, data) => {
                console.log(`Event: ${eventName}`, event, data);
            });
        }
    }

    /**
     * Post the event to other browser tabs through localstorage.
     * @param {string} eventName
     * @param {*} data
     * @private
     */
    _postMessage(eventName, data) {
        if (this.localStorage) {
            console.debug('Posting message via localStorage');

            this.localStorage.setItem('event:' + eventName, data ? JSON.stringify(data) : null);
        }
    }

    /**
     * @param {StorageEvent} event
     * @private
     */
    _onStorageMessageReceived(event) {
        const key = event.key;

        if (!key || key.substr(0, 6) !== 'event:') {
            return;
        }

        const eventName = key.substr(6);
        const data = event.newValue ? JSON.parse(event.newValue) : event.newValue;

        this.$rootScope.$broadcast(eventName, {
            ...data,
            $$target: TARGET_GLOBAL,
        });
    }
}
